import { useState, useMemo } from 'react';
import {
  ClipboardCheck,
  AlertTriangle,
  Plus,
  Search,
  CheckCircle,
  XCircle,
  Filter,
  FileWarning,
  Gauge,
  CheckCircle2,
  XOctagon,
  FileX2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TestResultDialog } from '@/components/quality/TestResultDialog';
import { NcrReportDialog } from '@/components/quality/NcrReportDialog';
import { StatusCard } from '@/components/dashboard/StatusCard';

// Mock data - replace with React Query hooks in production
const testResults = [
  {
    id: 1,
    wo_number: 'WO-2024-0045',
    product_name: 'NVIDIA System Unit',
    serial_number: 'SN-2024-00123',
    test_type: 'functional',
    result: 'pass',
    measured_value: '24.2V',
    expected_value: '24V ± 0.5V',
    tested_by: 'John Smith',
    tested_at: '2024-02-15T10:30:00',
  },
  {
    id: 2,
    wo_number: 'WO-2024-0045',
    product_name: 'NVIDIA System Unit',
    serial_number: 'SN-2024-00124',
    test_type: 'thermal',
    result: 'fail',
    measured_value: '78°C',
    expected_value: '< 70°C',
    tested_by: 'Jane Doe',
    tested_at: '2024-02-15T11:15:00',
    notes: 'Thermal compound needs reapplication',
  },
  {
    id: 3,
    wo_number: 'WO-2024-0044',
    product_name: 'Sensor Box Assembly',
    serial_number: 'SN-2024-00089',
    test_type: 'electrical',
    result: 'pass',
    measured_value: '12.1V',
    expected_value: '12V ± 0.2V',
    tested_by: 'John Smith',
    tested_at: '2024-02-14T16:00:00',
  },
  {
    id: 4,
    wo_number: 'WO-2024-0043',
    product_name: 'NVIDIA System Unit',
    serial_number: 'SN-2024-00118',
    test_type: 'visual',
    result: 'conditional',
    measured_value: 'Minor scratch on panel',
    expected_value: 'No visible defects',
    tested_by: 'Sarah Wilson',
    tested_at: '2024-02-14T14:30:00',
    notes: 'Approved for internal use only',
  },
];

const ncrReports = [
  {
    id: 1,
    ncr_number: 'NCR-2024-0012',
    product_sku: 'SYS-NVIDIA-001',
    product_name: 'NVIDIA System Unit',
    serial_number: 'SN-2024-00124',
    severity: 'major',
    defect_type: 'functional',
    description: 'Thermal performance exceeds specifications during stress test',
    root_cause: 'assembly',
    disposition: 'rework',
    status: 'open',
    reported_by: 'Jane Doe',
    created_at: '2024-02-15',
  },
  {
    id: 2,
    ncr_number: 'NCR-2024-0011',
    product_sku: 'CTRL-BOX-001',
    product_name: 'Control Box',
    serial_number: null,
    severity: 'minor',
    defect_type: 'cosmetic',
    description: 'Paint finish inconsistent on batch received from supplier',
    root_cause: 'supplier_issue',
    disposition: 'use_as_is',
    status: 'closed',
    reported_by: 'Mike Johnson',
    created_at: '2024-02-12',
  },
  {
    id: 3,
    ncr_number: 'NCR-2024-0010',
    product_sku: 'SENSOR-TEMP-01',
    product_name: 'Temperature Sensor',
    serial_number: null,
    severity: 'critical',
    defect_type: 'functional',
    description: 'Batch of 10 sensors failing calibration check',
    root_cause: 'material_defect',
    disposition: 'return_to_supplier',
    status: 'open',
    reported_by: 'John Smith',
    created_at: '2024-02-10',
  },
];

const workOrders = [
  { id: 1, wo_number: 'WO-2024-0045', product_name: 'NVIDIA System Unit' },
  { id: 2, wo_number: 'WO-2024-0044', product_name: 'Sensor Box Assembly' },
  { id: 3, wo_number: 'WO-2024-0043', product_name: 'NVIDIA System Unit' },
];

const products = [
  { id: 1, sku: 'SYS-NVIDIA-001', name: 'NVIDIA System Unit' },
  { id: 2, sku: 'SENSOR-BOX-001', name: 'Sensor Box Assembly' },
  { id: 3, sku: 'CHILLER-001', name: 'Industrial Chiller' },
  { id: 4, sku: 'CTRL-BOX-001', name: 'Control Box' },
  { id: 5, sku: 'SENSOR-TEMP-01', name: 'Temperature Sensor' },
];

const resultConfig = {
  pass: { label: 'Pass', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100' },
  fail: { label: 'Fail', icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' },
  conditional: { label: 'Conditional', icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-100' },
};

const severityConfig = {
  critical: { label: 'Critical', variant: 'destructive' as const },
  major: { label: 'Major', variant: 'warning' as const },
  minor: { label: 'Minor', variant: 'secondary' as const },
};

const dispositionConfig = {
  use_as_is: { label: 'Use As Is', color: 'text-blue-600' },
  rework: { label: 'Rework', color: 'text-orange-600' },
  scrap: { label: 'Scrap', color: 'text-red-600' },
  return_to_supplier: { label: 'Return to Supplier', color: 'text-purple-600' },
  pending: { label: 'Pending', color: 'text-muted-foreground' },
};

export function QualityPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [resultFilter, setResultFilter] = useState<string>('all');
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [ncrDialogOpen, setNcrDialogOpen] = useState(false);

  // Calculate quality metrics
  const metrics = useMemo(() => {
    const totalTests = testResults.length;
    const passCount = testResults.filter(t => t.result === 'pass').length;
    const failCount = testResults.filter(t => t.result === 'fail').length;
    const passRate = totalTests > 0 ? (passCount / totalTests) * 100 : 0;

    const openNcrs = ncrReports.filter(n => n.status === 'open').length;
    const criticalNcrs = ncrReports.filter(n => n.severity === 'critical' && n.status === 'open').length;

    return { totalTests, passCount, failCount, passRate, openNcrs, criticalNcrs };
  }, []);

  // Filter test results
  const filteredTests = useMemo(() => {
    return testResults.filter((test) => {
      const matchesSearch =
        test.wo_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        test.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (test.serial_number?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);

      const matchesResult = resultFilter === 'all' || test.result === resultFilter;

      return matchesSearch && matchesResult;
    });
  }, [searchQuery, resultFilter]);

  const handleTestSubmit = (data: any) => {
    console.log('Test Result:', data);
    setTestDialogOpen(false);
  };

  const handleNcrSubmit = (data: any) => {
    console.log('NCR Report:', data);
    setNcrDialogOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold">Quality Control</h2>
          <p className="text-muted-foreground">
            Track test results and manage non-conformance reports
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setNcrDialogOpen(true)}>
            <FileWarning className="h-4 w-4 mr-2" />
            Create NCR
          </Button>
          <Button onClick={() => setTestDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Record Test
          </Button>
        </div>
      </div>

      {/* Quality Metrics - Modern Floating Design */}
      <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        <StatusCard
          title="Pass Rate"
          value={`${metrics.passRate.toFixed(1)}%`}
          subtext={metrics.passRate >= 95 ? 'Excellent' : metrics.passRate >= 85 ? 'Good' : 'Needs improvement'}
          trend={metrics.passRate >= 95 ? 'up' : metrics.passRate >= 85 ? 'neutral' : 'down'}
          type="custom"
          icon={Gauge}
          iconColor="text-blue-500"
          iconBgColor="bg-blue-500/10"
          gradientColor="blue"
        />

        <StatusCard
          title="Tests Passed"
          value={metrics.passCount.toString()}
          subtext={`${metrics.totalTests} total tests`}
          trend={metrics.passCount > 0 ? 'up' : 'neutral'}
          type="custom"
          icon={CheckCircle2}
          iconColor="text-green-500"
          iconBgColor="bg-green-500/10"
          gradientColor="green"
        />

        <StatusCard
          title="Tests Failed"
          value={metrics.failCount.toString()}
          subtext={metrics.failCount === 0 ? 'No failures' : 'Requires attention'}
          trend={metrics.failCount === 0 ? 'up' : 'down'}
          type="custom"
          icon={XOctagon}
          iconColor="text-red-500"
          iconBgColor="bg-red-500/10"
          gradientColor="red"
        />

        <StatusCard
          title="Open NCRs"
          value={metrics.openNcrs.toString()}
          subtext={metrics.openNcrs === 0 ? 'All resolved' : 'Pending review'}
          trend={metrics.openNcrs === 0 ? 'up' : 'neutral'}
          type="custom"
          icon={FileWarning}
          iconColor="text-orange-500"
          iconBgColor="bg-orange-500/10"
          gradientColor="orange"
        />

        <StatusCard
          title="Critical NCRs"
          value={metrics.criticalNcrs.toString()}
          subtext={metrics.criticalNcrs === 0 ? 'No critical issues' : 'Urgent action'}
          trend={metrics.criticalNcrs === 0 ? 'up' : 'down'}
          type="custom"
          icon={FileX2}
          iconColor="text-rose-500"
          iconBgColor="bg-rose-500/10"
          gradientColor="rose"
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="tests" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tests" className="gap-2">
            <ClipboardCheck className="h-4 w-4" />
            Test Results
          </TabsTrigger>
          <TabsTrigger value="ncr" className="gap-2">
            <FileWarning className="h-4 w-4" />
            NCR Reports
            {metrics.openNcrs > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 px-1.5">
                {metrics.openNcrs}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Test Results Tab */}
        <TabsContent value="tests" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by WO, product, or serial..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={resultFilter} onValueChange={setResultFilter}>
              <SelectTrigger className="w-[150px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Result" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Results</SelectItem>
                <SelectItem value="pass">Pass</SelectItem>
                <SelectItem value="fail">Fail</SelectItem>
                <SelectItem value="conditional">Conditional</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Test Results Table */}
          <div className="rounded-lg border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left text-sm font-medium">Work Order</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Product</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Serial #</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Test Type</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Result</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Values</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Tested By</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTests.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                        No test results found
                      </td>
                    </tr>
                  ) : (
                    filteredTests.map((test) => {
                      const config = resultConfig[test.result as keyof typeof resultConfig];
                      const ResultIcon = config.icon;

                      return (
                        <tr
                          key={test.id}
                          className={cn(
                            'border-b hover:bg-muted/30',
                            test.result === 'fail' && 'bg-red-50/50'
                          )}
                        >
                          <td className="px-4 py-3 text-sm font-mono text-primary">
                            {test.wo_number}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">{test.product_name}</td>
                          <td className="px-4 py-3 text-sm font-mono text-muted-foreground">
                            {test.serial_number || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm capitalize">{test.test_type}</td>
                          <td className="px-4 py-3 text-sm text-center">
                            <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium', config.bg, config.color)}>
                              <ResultIcon className="h-3 w-3" />
                              {config.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="text-xs">
                              <span className="text-muted-foreground">Measured: </span>
                              <span className="font-medium">{test.measured_value}</span>
                            </div>
                            <div className="text-xs">
                              <span className="text-muted-foreground">Expected: </span>
                              <span>{test.expected_value}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {test.tested_by}
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {new Date(test.tested_at).toLocaleDateString()}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* NCR Reports Tab */}
        <TabsContent value="ncr" className="space-y-4">
          {/* NCR Filters */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search NCRs..." className="pl-10" />
            </div>

            <Select defaultValue="open">
              <SelectTrigger className="w-[150px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* NCR Table */}
          <div className="rounded-lg border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left text-sm font-medium">NCR #</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Product</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Severity</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Defect Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Disposition</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {ncrReports.map((ncr) => {
                    const severity = severityConfig[ncr.severity as keyof typeof severityConfig];
                    const disposition = dispositionConfig[ncr.disposition as keyof typeof dispositionConfig];

                    return (
                      <tr
                        key={ncr.id}
                        className={cn(
                          'border-b hover:bg-muted/30',
                          ncr.severity === 'critical' && ncr.status === 'open' && 'bg-red-50/50'
                        )}
                      >
                        <td className="px-4 py-3 text-sm font-mono text-primary">
                          {ncr.ncr_number}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <p className="font-medium">{ncr.product_name}</p>
                          <p className="text-xs font-mono text-muted-foreground">{ncr.product_sku}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <Badge variant={severity.variant}>{severity.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm capitalize">
                          {ncr.defect_type.replace('_', ' ')}
                        </td>
                        <td className="px-4 py-3 text-sm max-w-xs truncate" title={ncr.description}>
                          {ncr.description}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={disposition.color}>{disposition.label}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <Badge variant={ncr.status === 'open' ? 'warning' : 'secondary'}>
                            {ncr.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {ncr.created_at}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <TestResultDialog
        open={testDialogOpen}
        onOpenChange={setTestDialogOpen}
        workOrders={workOrders}
        onSubmit={handleTestSubmit}
      />

      <NcrReportDialog
        open={ncrDialogOpen}
        onOpenChange={setNcrDialogOpen}
        products={products}
        workOrders={workOrders}
        onSubmit={handleNcrSubmit}
      />
    </div>
  );
}
